import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Grid,
  Button,
  List,
  Box,
  TextField,
  SwipeableDrawer,
  FormControl,
  InputLabel,
  Typography,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Tooltip,
} from "@material-ui/core";
import "react-calendar/dist/Calendar.css";
import "../../../css/employee-page.css";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import useStyles from "../../../themes/style.js";
import PageTitle from "../../../components/PageTitle/PageTitle.js";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { BsFileEarmarkExcel } from "react-icons/bs";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import "react-big-calendar/lib/css/react-big-calendar.css";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { useNotification } from "../../../hooks/useNotification.js";
import axios from "axios";
import * as XLSX from "xlsx";
import xlsxIcon from "../../../images/employee/microsoft-excel-icon.png";

const AdminCalendarUI = () => {
  const classes = useStyles();
  const token = localStorage.getItem("token");
  const handleNotificationCall = useNotification();

  const [loader, setLoader] = useState(false);

  const [excelUploadOpen, setExcelUploadOpen] = useState(false);
  const [file, setFile] = useState([]);
  const [addLeaveModal, setAddLeaveModal] = useState(false);
  const [excelLeaveDates, setExcelLeaveDates] = useState([]);
  const [leaveDates, setLeaveDates] = useState([]);

  const handleOpenLeaveModal = (item) => {
    setAddLeaveModal(true);
  };

  const handleCloseLeaveModal = () => {
    setAddLeaveModal(false);
  };

  const handleExcelUploadOpenModal = (item) => {
    setExcelUploadOpen(true);
  };

  const handleExcelUploadCloseModal = () => {
    setExcelUploadOpen(false);
    setFile([]);
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    date: Yup.string().required("Leave Date is required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  async function handleAdd(values) {
    setLoader(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}leave/addLeaveForMonth`,
        {
          title: values.title,
          dates: [moment(values.date).format("YYYY-MM-DD")],
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      setLoader(false);
      if (response.data.status) {
        handleNotificationCall("success", response.data.message);
        handleCloseLeaveModal();
        fetchAllLeaveDetails();
      } else {
        handleNotificationCall("error", response.data.message);
      }
    } catch (error) {
      setLoader(false);
      console.error(error);
    }
  }

  useEffect(() => {
    fetchAllLeaveDetails();
  }, []);

  const fetchAllLeaveDetails = async () => {
    setLoader(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}leave/viewAllLeaveForMonth`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      setLoader(false);
      if (response.data.status) {
        const responseData = response.data?.data;
        const modifiedResponseData = responseData.map((item) => ({
          title: item.title,
          start: new Date(moment(item.date).format("YYYY-MM-DD")),
          end: new Date(moment(item.date).format("YYYY-MM-DD")),
        }));
        setLeaveDates(modifiedResponseData);
      } else {
        handleNotificationCall("error", response.data.message);
      }
    } catch (error) {
      setLoader(false);
      console.error(error);
    }
  };

  const downloadSampleFile = () => {
    window.location.href = require("../../../images/files/leaveDate.xlsx");
  };

  const uploadAndConvertXlsx = async (event) => {
    console.log(event);
    try {
      const target = event.target;
      const file = target.files[0];
      setLoader(true);

      if (!file.name.endsWith(".xlsx")) {
        handleCloseLeaveModal();
        setLoader(false);
        handleNotificationCall(
          "error",
          "Invalid file format. Please select an XLSX file",
        );
        return;
      }

      if (file) {
        const reader = new FileReader();

        reader.onload = (e) => {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const records = XLSX.utils.sheet_to_json(sheet);

          console.log("Data Records:", records);
          setExcelLeaveDates(records);
        };

        reader.readAsBinaryString(file);
        console.log("Records:", reader.readAsBinaryString(file));
      } else {
        handleNotificationCall("error", "Only choose xlsx format");
        setLoader(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setLoader(false);
    }
  };

  function ExcelDateToJSDate(serial) {
    const utc_days = Math.floor(serial - 25569); // Days since 1970-01-01
    const utc_value = utc_days * 86400; // Convert days to seconds
    const date_info = new Date(utc_value * 1000); // Create UTC date

    // Format the date as DD-MM-YYYY
    const day = String(date_info.getDate()).padStart(2, "0");
    const month = String(date_info.getMonth() + 1).padStart(2, "0");
    const year = date_info.getFullYear();

    return `${year}-${month}-${day}`;
  }

  async function handleUpload() {
    if (excelLeaveDates.length <= 0) {
      handleNotificationCall("error", "select the file first");
      return;
    }

    setLoader(true);
    try {
      const holidaysWithFormattedDates = excelLeaveDates.map((holiday) => ({
        ...holiday,
        dates: [ExcelDateToJSDate(holiday.date)],
      }));

      console.log(holidaysWithFormattedDates);

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}leave/addBulkLeaveForMonth`,
        {
          data: holidaysWithFormattedDates,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      setLoader(false);
      if (response.data.status) {
        handleNotificationCall("success", response.data.message);
        handleExcelUploadCloseModal();
        fetchAllLeaveDetails();
      } else {
        handleNotificationCall("error", response.data.message);
      }
    } catch (error) {
      setLoader(false);
      console.error(error);
    }
  }

  const locales = {
    "en-US": require("date-fns/locale/en-US"),
  };
  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  });

  const events = [
    {
      title: "Flood Leave",
      start: new Date("2024-12-12"),
      end: new Date("2024-12-12"),
    },
    {
      title: "Government Leave",
      start: new Date("2024-12-18"),
      end: new Date("2024-12-18"),
    },
  ];

  return (
    <div style={{ marginTop: "20px" }}>
      <Grid container direction="row" spacing={2} className={classes.heading}>
        <Grid item xs={12} sm={6} md={8} lg={6}>
          <PageTitle title="Office Calendar" />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={6} className={classes.drawerClose}>
          <div style={{display:"flex",gap:"12px",flexDirection:"row",flexWrap:"wrap",justifyContent:"end",marginBottom:'10px' }}>
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<BsFileEarmarkExcel />}
              className={classes.addUser}
              style={{ marginRight: "5px", background: "#117c44", color: "#fff" }}
              onClick={handleExcelUploadOpenModal}
            >
              Add Bulk Leave
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={<AddCircleIcon />}
              className={classes.addUser}
              onClick={handleOpenLeaveModal}
            >
              Add Leave
            </Button>
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Calendar
          views={["day", "week", "month"]}
          localizer={localizer}
          events={leaveDates}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "80vh", backgroundColor: "white", width: "100%" }}
          eventPropGetter={(event) => {
            const style = {
              backgroundColor: "#EDF3FF",
              borderLeft: "5px solid #2C6BED",
              color: "black",
              padding: "5px",
            };
            return { style };
          }}
        />
      </Grid>

      <Dialog
        onClose={handleExcelUploadCloseModal}
        aria-labelledby="dialog-title"
        open={excelUploadOpen}
        width="xs"
        maxWidth="xs"
        PaperProps={{ style: { width: "100%" } }}
      >
        <DialogTitle className={classes.digTitle}>
          <div className={classes.center}>
            <Typography
              variant="subtitle2"
              className={classes.digColor + " " + classes.digCenter}
            >
              Upload Leave Details
            </Typography>
            <div className={classes.drawerClose}>
              <CloseIcon
                className={classes.digClose}
                size="14px"
                onClick={handleExcelUploadCloseModal}
              />
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid className={classes.center}>
            {/* <a href={sample_candidates} className={classes.aLink} download>
              Sample excel format to upload your leave details
            </a> */}
            <Tooltip
              title="Dowmload Sample"
              placement="right"
              aria-label="download"
            >
              <Box
                p={2}
                style={{
                  background: "#d8d8d8",
                  borderRadius: "8px",
                  border: "1px solid #a6a6a6",
                  cursor: "pointer",
                }}
                onClick={downloadSampleFile}
              >
                <img
                  src={xlsxIcon}
                  width="100%"
                  style={{ width: "64px", height: "64px" }}
                  alt="sample"
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid className={classes.center} style={{ paddingTop: "20px" }}>
            <input
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              className={classes.input}
              id="icon-button-csv"
              type="file"
              style={{ display: "none" }}
              onChange={(e) => {
                uploadAndConvertXlsx(e);
              }}
            />
            <label htmlFor="icon-button-csv">
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                startIcon={<CloudUploadIcon />}
                aria-label="upload Backup"
                component="span"
              >
                Upload Excel
              </Button>
            </label>
          </Grid>

          <Grid className={classes.center + " " + classes.button}>
            <Typography variant="inherit" className={classes.lineBreak}>
              {file?.name}
            </Typography>
          </Grid>

          <Grid className={classes.sendWhatsapp}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleUpload}
            >
              Submit
            </Button>

            <Button
              variant="contained"
              size="small"
              color="secondary"
              onClick={handleExcelUploadCloseModal}
            >
              Close
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addLeaveModal}
        fullWidth
        maxWidth="xs"
        onClose={handleCloseLeaveModal}
        aria-labelledby="form-dialog-title"
      >
        <form onSubmit={handleSubmit(handleAdd)}>
          <h3 style={{ padding: "12px 12px", margin: 0 }}>Add leave</h3>
          <DialogContent>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} md={12} lg={12}>
                <InputLabel shrink htmlFor="title">
                  Title
                </InputLabel>
                <FormControl className={classes.margin}>
                  <TextField
                    size="small"
                    InputProps={{ disableUnderline: true }}
                    classes={{ root: classes.customTextField }}
                    placeholder="Enter Leave Title"
                    name="title"
                    {...register("title")}
                    error={errors.title ? true : false}
                  />

                  <Typography variant="inherit" color="error">
                    {errors.title?.message}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <InputLabel shrink htmlFor="date">
                  Leave Date
                </InputLabel>
                <FormControl className={classes.margin}>
                  <TextField
                    type="date"
                    size="small"
                    InputProps={{ disableUnderline: true }}
                    classes={{ root: classes.customTextField }}
                    placeholder="Enter your Feedback Message..."
                    name="date"
                    {...register("date")}
                    error={errors.date ? true : false}
                  />

                  <Typography variant="inherit" color="error">
                    {errors.date?.message}
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions style={{ padding: "12px 24px" }}>
            <Button
              onClick={handleCloseLeaveModal}
              variant="contained"
              color="secondary"
              size="small"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              type="submit"
            >
              Send
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Backdrop className={classes.backdrop} open={loader}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default AdminCalendarUI;
